// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useCallback, useMemo, useState } from "react";
import type { TourStep } from "../dto/tourModels";

export interface TutorialDetailState {
  detailIndex: number;
  trigger: string;
  steps: readonly TourStep[];
  index: number;
  currentStep: TourStep | undefined;
  total: number;
  isFirst: boolean;
  isLast: boolean;
}

export interface TutorialTourState {
  currentIndex: number;
  currentStep: TourStep | undefined;
  total: number;
  isFirst: boolean;
  isLast: boolean;
  advance: () => void;
  back: () => void;
  selectIndex: (index: number) => void;
  reset: () => void;
  detail: TutorialDetailState | null;
  isInDetail: boolean;
  openDetail: (detailIndex: number) => void;
  closeDetail: () => void;
  advanceDetail: () => void;
  backDetail: () => void;
  selectDetailIndex: (index: number) => void;
  finishDetail: () => void;
}

function clampIndex(index: number, total: number): number {
  return Math.min(Math.max(index, 0), Math.max(total - 1, 0));
}

function detailStepAt(
  steps: readonly TourStep[],
  index: number,
): TourStep | undefined {
  return steps[index] as TourStep | undefined;
}

function moveDetail(
  d: TutorialDetailState,
  target: number,
): TutorialDetailState {
  const next = clampIndex(target, d.total);
  return {
    index: next,
    detailIndex: d.detailIndex,
    trigger: d.trigger,
    steps: d.steps,
    total: d.total,
    currentStep: detailStepAt(d.steps, next),
    isFirst: next === 0,
    isLast: next === d.total - 1,
  };
}

export function useTutorialTour(steps: readonly TourStep[]): TutorialTourState {
  const [index, setIndex] = useState(0);
  const [detail, setDetail] = useState<TutorialDetailState | null>(null);

  const total = steps.length;

  const advance = useCallback(() => {
    setIndex((current) => clampIndex(current + 1, total));
  }, [total]);

  const back = useCallback(() => {
    setIndex((current) => clampIndex(current - 1, total));
  }, [total]);

  const selectIndex = useCallback(
    (index: number) => {
      setIndex(clampIndex(index, total));
    },
    [total],
  );

  const reset = useCallback(() => {
    setIndex(0);
  }, []);

  const currentStep = steps[index];

  const openDetail = useCallback(
    (detailIndex: number) => {
      const entry = currentStep?.details?.[detailIndex];
      if (!entry || entry.steps.length === 0) {
        return;
      }
      const sub = entry.steps;
      setDetail({
        detailIndex,
        trigger: entry.trigger,
        steps: sub,
        index: 0,
        currentStep: sub[0],
        total: sub.length,
        isFirst: true,
        isLast: sub.length === 1,
      });
    },
    [currentStep],
  );

  const closeDetail = useCallback(() => {
    setDetail(null);
  }, []);

  const advanceDetail = useCallback(() => {
    setDetail((d) => (d ? moveDetail(d, d.index + 1) : d));
  }, []);

  const backDetail = useCallback(() => {
    setDetail((d) => (d ? moveDetail(d, d.index - 1) : d));
  }, []);

  const selectDetailIndex = useCallback((target: number) => {
    setDetail((d) => (d ? moveDetail(d, target) : d));
  }, []);

  const finishDetail = useCallback(() => {
    setDetail(null);
    setIndex((current) => clampIndex(current + 1, total));
  }, [total]);

  const state = useMemo<TutorialTourState>(
    () => ({
      currentIndex: index,
      currentStep,
      total,
      isFirst: index === 0,
      isLast: index === total - 1,
      advance,
      back,
      selectIndex,
      reset,
      detail,
      isInDetail: detail !== null,
      openDetail,
      closeDetail,
      advanceDetail,
      backDetail,
      selectDetailIndex,
      finishDetail,
    }),
    [
      index,
      currentStep,
      total,
      advance,
      back,
      selectIndex,
      reset,
      detail,
      openDetail,
      closeDetail,
      advanceDetail,
      backDetail,
      selectDetailIndex,
      finishDetail,
    ],
  );

  return state;
}
